
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { IOption } from '@/core/models/AntDesign/IOption';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import ServicoCipEstrutura from '@/servicos/Cadastros/PCP/ServicoCipEstrutura';

export default defineComponent({
  name: 'SelecionarCipEstrutura',
  props: {
    codigoSelecionado: {
      type: Number,
      default: 0,
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Selecione',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    codigoTipoProducao: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update:codigoSelecionado', 'update:descricaoSelecionado', 'change'],
  setup(props, { emit }) {
    const { montaOpcoesComListaConsultaRapida } = useTelaBase();
    const refCipEstrutura = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servicoCipEstrutura = new ServicoCipEstrutura();
    servicoCipEstrutura.requisicaoSistema();

    const state = reactive({
      listaEstruturas: [] as IOption[],
      buscandoDados: false,
      emDigitacao: false,
    });

    const computedDescricaoSelecionado = computed({
      get: () => props.descricaoSelecionado,
      set: (val: string) => {
        emit('update:descricaoSelecionado', val);
      },
    });

    async function obterCipEstruturaPorCodigo() {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.validaCodigo(props.codigoSelecionado)) { return; }

      const existeLista = state.listaEstruturas.find((c) => c.value === props.codigoSelecionado);
      if (existeLista !== undefined) {
        computedDescricaoSelecionado.value = existeLista.label;
        return;
      }

      const estrutura = await servicoCipEstrutura.obterPorCodigo(props.codigoSelecionado);
      if (estrutura !== undefined && estrutura !== null) {
        state.listaEstruturas.push({
          value: estrutura.codigo,
          label: estrutura.descricao,
        });
        computedDescricaoSelecionado.value = estrutura.descricao;
      }
    }

    async function pesquisarCipEstrutura(valorPesquisa: string) {
      state.emDigitacao = true;
      state.buscandoDados = true;

      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        if (UtilitarioGeral.validaCodigo(props.codigoTipoProducao)) {
          const listaEstruturas = await servicoCipEstrutura.listarPorTipoProducao(props.codigoTipoProducao);
          const termo = (valorPesquisa || '').toLowerCase();
          const listaFiltrada = listaEstruturas.filter((item) => termo === ''
            || `${item.codigo}`.includes(termo)
            || (item.descricao !== undefined && item.descricao.toLowerCase().includes(termo)));

          state.listaEstruturas = listaFiltrada.map((item) => ({
            value: item.codigo,
            label: item.descricao,
          }));
        } else {
          const parametrosConsultaRapida: IParametrosConsultaRapida = {
            valor: valorPesquisa,
            apenasAtivos: true,
            recursoAssociado: '',
            quantidadeRegistros: 100,
          };

          if (UtilitarioGeral.validaLista(props.empresas)) {
            parametrosConsultaRapida.empresas = props.empresas;
          } else {
            parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
          }

          const listaConsultaRapida = await servicoCipEstrutura.consultaRapida(parametrosConsultaRapida);
          state.listaEstruturas = montaOpcoesComListaConsultaRapida(listaConsultaRapida);
        }

        state.buscandoDados = false;
      }, 500);
    }

    const computedCodigoSelecionado = computed<number | undefined>({
      get: () => {
        obterCipEstruturaPorCodigo();
        return props.codigoSelecionado === 0 ? undefined : props.codigoSelecionado;
      },
      set: (val: number | undefined) => {
        emit('update:codigoSelecionado', val === undefined ? 0 : val);
        const option = state.listaEstruturas.find((c) => c.value === val);
        computedDescricaoSelecionado.value = option !== undefined ? option.label : '';
      },
    });

    function change() {
      emit('change', props.codigoSelecionado);
    }

    function focus() {
      if (refCipEstrutura.value) {
        refCipEstrutura.value.focus();
      }
    }

    return {
      props,
      state,
      refCipEstrutura,
      pesquisarCipEstrutura,
      computedCodigoSelecionado,
      computedDescricaoSelecionado,
      change,
      focus,
    };
  },
});
